import React, { ChangeEvent, KeyboardEvent, useEffect } from 'react';
import { useStyles } from './ZipInput.styles';
import { Icon, Icons } from 'components/Global/Icon/Icon';
import { appPalette } from 'theme/App.palette';
import {
  ClickAwayListener,
  Grid,
  IconButton,
  Input,
  useTheme,
} from '@mui/material';
import { useState, useRef } from 'react';
import { useValidateZipcode } from 'hooks/useValidateZipcode';
import TextRender from 'components/Global/TextRender/TextRender';
import { useDispatch, useSelector } from 'react-redux';
import { selectZipcode, setZipcode } from 'redux/slices/zipcodeSlice';
import { LinkButton } from 'components/Global/LinkButton/LinkButton';
import { IDealerLocatorLabels } from 'interfaces/dealerLocator';
export interface IZipInput {
  onChange: Function;
  labels: IDealerLocatorLabels;
  isMobileHidden?: boolean; // Used to determine when
}
export function ZipInput({
  onChange,
  labels,
  isMobileHidden,
}: IZipInput): JSX.Element {
  const classes = useStyles();
  const zipcode = useSelector(selectZipcode);
  const theme = useTheme();
  const isHondaTheme = theme.themeName === 'honda';
  const { iconMainColor } = appPalette.colors;
  const { white } = appPalette.colors;
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [newZipcode, setNewZipcode] = useState('');
  const inputRef = useRef<HTMLInputElement>();
  const dispatch = useDispatch(); // redux dispatch
  const { result, error, isLoading, fetchData, coordinates } =
    useValidateZipcode(newZipcode, labels?.zipcodeError);

  const handleCloseForm = () => {
    if (isFormOpen && !isLoading) {
      setIsFormOpen(false);
    }
  };

  const handleIsFormOpen = () => {
    setIsFormOpen(!isFormOpen);
  };

  const handleFieldChange = (e: ChangeEvent) => {
    const input = inputRef.current;
    if (input) {
      const antiRegex = new RegExp(/[^0-9]+/g);
      const validZipcode = new RegExp(/^\d{5}$/);
      input.value = input.value.replace(antiRegex, '');

      if (input.value.match(validZipcode)) {
        setNewZipcode(input.value);
      } else {
        setNewZipcode('');
      }
    }
  };

  useEffect(() => {
    if (result && !error) {
      dispatch(setZipcode(newZipcode));
      if (coordinates) {
        onChange(coordinates);
      }
      if (isFormOpen) {
        setIsFormOpen(false);
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, coordinates]);

  const handleNewZipcodeClick = () => {
    if (newZipcode.length > 0) {
      fetchData();
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !isLoading && newZipcode.length > 0) {
      handleNewZipcodeClick();
    }
  };

  function autofocus(item: HTMLElement) {
    if (item) {
      if (isFormOpen) {
        item.focus();
      }
    }
  }

  return (
    <ClickAwayListener onClickAway={handleCloseForm}>
      <Grid
        container
        direction="row"
        className={`zip-input ${classes.root} ${
          isMobileHidden ? 'mobile-hidden' : ''
        }`}
      >
        <Grid
          container
          direction="row"
          className={`enter-zip ${
            isFormOpen
              ? `${classes.show_grid} displayed`
              : `${classes.hide_grid} hidden`
          }`}
        >
          <Input
            inputProps={{
              maxLength: 5,
              type: 'text',
              className: `${isHondaTheme ? 'atm-label-1' : 'acr-paragraph-9'} ${
                error ? 'error' : ''
              }`,
              ref: inputRef,
              'aria-label': labels?.enterZip,
            }}
            inputRef={autofocus}
            className={`${isHondaTheme ? 'atm-label-1' : 'acr-paragraph-9'} ${
              classes.zip_input
            } zip-input-field`}
            data-automation="zip-input"
            placeholder={labels?.enterZip}
            onChange={handleFieldChange}
            disableUnderline={true}
            onKeyDown={handleKeyDown}
          />
          <LinkButton
            onClick={() => {
              !isLoading && newZipcode.length > 0 && handleNewZipcodeClick();
            }}
            classNames={{
              container: `change-zip ${classes.form_button} ${
                !isLoading && newZipcode.length > 0 ? 'is-enabled' : ''
              }`,
            }}
            ariaLabel="Submit ZIP code"
          >
            {isLoading ? (
              <span className={classes.spinner}></span>
            ) : (
              <Icon isDecorative type={Icons.arrowStrong} color={white} />
            )}
          </LinkButton>
          {error && (
            <span className={`${classes.error} error-msg acr-paragraph-11`}>
              <TextRender text={error} />
            </span>
          )}
        </Grid>
        <Grid
          container
          direction="row"
          className={`your-location ${
            isFormOpen
              ? `${classes.hide_grid} hidden`
              : `${classes.show_grid} displayed`
          }`}
        >
          <IconButton
            disableRipple
            onClick={handleIsFormOpen}
            tabIndex={0}
            data-automation="your-location-input"
            className={`${isHondaTheme ? 'atm-label-1' : 'acr-paragraph-9'} ${
              classes.zip_input_button
            } ${isFormOpen ? classes.is_form_open : ''}`}
            aria-label="Zip Code"
          >
            <span
              className={`cta-focusable ${classes.focusable}`}
              tabIndex={-1}
            ></span>
            <Icon isDecorative type={Icons.compass} color={iconMainColor} />
            <TextRender
              text={
                !isFormOpen
                  ? `${labels?.zipcodeLabel} ${zipcode || ''}`
                  : labels?.zipcodeCurrentLocation
              }
            />
          </IconButton>
        </Grid>
      </Grid>
    </ClickAwayListener>
  );
}
